import { useCallback, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { OneLineCellStyles } from "@/controls/ag-grid/OneLineCells";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { PassId } from "../models/PassId";
import { useTableData } from "../hooks/useTableData";

interface PassIdsGridProps {
	passIds: PassId[];
	selectedPassId: PassId | null;
	onSelectPassId: (passId: PassId | null) => void;
	onDelete: (passId: PassId) => void;
}

export const PassIdsGrid = ({
	passIds,
	selectedPassId,
	onSelectPassId,
	onDelete,
}: PassIdsGridProps) => {
	const gridRef = useRef<AgGridReact<PassId>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback((_params: GridReadyEvent<PassId>) => {
		// Let flex columns handle sizing naturally
	}, []);

	const handleRowClicked = useCallback(
		(event: RowClickedEvent<PassId>) => {
			if (event.data) {
				onSelectPassId(event.data);
			}
		},
		[onSelectPassId],
	);

	const getRowId = useCallback((params: { data: PassId }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<PassId>
				ref={gridRef}
				rowData={passIds}
				columnDefs={columnDefs}
				defaultColDef={globalDefaultColDef}
				onGridReady={onGridReady}
				onRowClicked={handleRowClicked}
				getRowId={getRowId}
				rowSelection="single"
				suppressCellFocus
				suppressContextMenu
				alwaysShowVerticalScroll
				alwaysShowHorizontalScroll
				suppressColumnVirtualisation
				rowHeight={36}
				headerHeight={36}
				getRowStyle={(params) => {
					if (selectedPassId?.Id === params.data?.Id) {
						return { backgroundColor: "#e3f2fd", color: "inherit" };
					}
					if (params.data && !params.data.Active) {
						return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
					}
					return undefined;
				}}
			/>
		</>
	);
};
